// IR-1226 P1-B: show/hide columns for the Avatars table, persisted per-browser
// in localStorage. The Avatar (name) column and the actions column are always
// visible; everything else is toggleable.

export type AvatarColumnKey =
  | "platform"
  | "status"
  | "ingestion"
  | "collection"
  | "groups"
  | "delivering"
  | "collected"
  | "archived"
  | "not_collected"
  | "analyst"
  | "classification"
  | "geo"
  | "proxy"
  | "scraper"
  | "last_seen"
  | "last_collection"
  | "load"
  | "created"
  | "notes"
  | "cover"
  | "imei"
  | "imei_2"
  | "model";

// IR-1502: ONE constant, because the name is not settled. The Avatars tab already
// shows a "capacity" — IR-1226's group-count limit — and this column is a
// different one: whether the avatar can MOVE what it takes in. Rotem has not yet
// answered c61842 ("Groups" for the limit, "Load" for this); change it here only.
export const LOAD_COLUMN_LABEL = "Load";

export const AVATAR_COLUMNS: { key: AvatarColumnKey; label: string }[] = [
  { key: "platform", label: "Platform" },
  { key: "status", label: "Status" },
  // IR-1327 r5 (Rotem, 2026-08-09): "its not 'stop control' - its 'Collection'
  // ... the actual columns order should be the same as the actual process
  // stages: Status, Collection, Ingestion". The rename half is settled here;
  // the ORDER half was superseded by her IR-1479 r3 list (see `ingestion`).
  //
  // The r4 rename to "Stop control" was an honest description of what the
  // column's INPUT knows (only whether an operator pressed Stop) but it named
  // the mechanism rather than the stage an operator is reading down the row.
  // So the header goes back to the stage name. The value vocabulary (r5's
  // "Not stopped is a state we shouldn't be in") is a separate, larger change
  // awaiting her sign-off.
  //
  // The key stays `collection` so per-browser saved column preferences survive
  // both renames.
  { key: "collection", label: "Collection" },
  // IR-1697 r14 (pre-QA F1; Mahdi 66145 "NOT FOUND"): Ingestion carries the
  // per-environment "Last data received ... UTC" text and sat after Archived,
  // past the horizontal fold at 1440. It is back directly after Collection (the
  // IR-1327 r5 process-stage position), ahead of the count columns. IR-1479 r3
  // put it after Archived; every other column of her order keeps its place.
  // Only KEYS are persisted, so a saved list needs no migration.
  { key: "ingestion", label: "Ingestion" },
  // IR-1479 r3 (Rotem, 2026-09-07) set the order "Avatar · platform · Status ·
  // Collection · Groups · collected groups · Delivering · archived · ingestion ·
  // the rest". IR-1610 (Rotem, 2026-09-14) moves ONE column out of it: "move the
  // old Groups column to be last in table". Everything between Collection and
  // `ingestion` keeps her r3 order; `groups` is now the final entry of the array.
  // IR-1426 (Rotem, 2026-08-23): the phone's own group count — her word,
  // "Collected groups". IR-1479 r3 moved it to directly after Groups (it used
  // to sit after Delivering so that IR-1400 r6's "immediately after Groups"
  // pair stayed adjacent); her 2026-09-07 list puts the two populations —
  // what we assigned, what the phone holds — side by side first.
  { key: "collected", label: "Collected groups" },
  // IR-1400 r6 (Rotem, 2026-08-23): delivering moves OUT of the Groups cell.
  // Stacking "8 of 19" + capacity pill + "19 / 60" in one cell was unreadable —
  // the two numbers answer different questions ("how much coverage is claimed"
  // vs "how much of it is real"), so each gets its own sortable column.
  { key: "delivering", label: "Delivering" },
  // IR-1487 (Rotem, 2026-09-04): "the archived should be counted separately and
  // not under collected groups because they are not collected - separate column
  // please". Never folded into the Collected cell, which is the literal ask.
  { key: "archived", label: "Archived" },
  // IR-1479: the shortfall — groups the phone reports that the last completed
  // cycle did not collect. It is the only count on the row that can be non-zero
  // when something is WRONG: Groups, Collected groups and Archived are all
  // structurally incapable of showing a gap. IR-1479 r3: Rotem's explicit
  // order did not name this column, so it leads "the rest", directly after
  // Ingestion.
  { key: "not_collected", label: "Not collected" },
  // IR-1502 (Rotem, 2026-09-07, c61810): "Show the TREND, not the level ... and
  // color it if it never drains." Her IR-1479 r3 order does not name it, so it
  // joins "the rest" directly after Not collected — both are "is this avatar
  // keeping up?" readings — and every column she DID place keeps its position.
  { key: "load", label: LOAD_COLUMN_LABEL },
  // IR-1696 (Rotem: "the most critical 5 min bug"): which analyst this avatar
  // is distributed to. Directly BEFORE Classification, not between it and
  // Geo — IR-1351 pins Geo immediately after Classification and this must not
  // disturb that adjacency.
  { key: "analyst", label: "Analyst" },
  { key: "classification", label: "Classification" },
  // IR-1351: Rotem asked for Geo "next to classification" — the order of this
  // array IS the column order, so adjacency is declared here, not in the JSX.
  { key: "geo", label: "Geo" },
  // IR-1351 item 4 — VM-reported, WhatsApp-only in practice.
  { key: "proxy", label: "Proxy" },
  // IR-1385: which scraper generation (v1/v2) the avatar runs. Sits beside
  // Proxy because both answer "what is actually running this account on the
  // VM?" rather than describing the persona.
  { key: "scraper", label: "Scraper" },
  // IR-1351: Notes sits BEFORE Last Seen / Created deliberately. It used to be
  // last of the default-visible columns, which at a normal window width put it
  // past the horizontal fold with the actions cell pinned over the right edge —
  // so the toolbar search matched a note and returned a row whose only visible
  // reason for being in the result set was off-screen. That is the same defect
  // as hiding it behind the Columns menu (IR-1233 r6), just produced by width
  // instead of visibility. Last Seen and Created are scannable-on-demand; the
  // searched-and-highlighted column has to be on screen.
  { key: "notes", label: "Notes" },
  { key: "last_seen", label: "Last Seen" },
  // IR-1515 (Rotem, 2026-09-07): "add Date for disconnected ... we lost 3
  // avatars over the weekend and I cant tell which!!!" Adjacent to Last Seen
  // because the two are read in one glance and are NOT the same fact: Last Seen
  // is the last time we heard from the agent at all (for a disconnected avatar
  // it is usually the relink stamp — S9_1 read 2026-08-25 while it collected
  // until 2026-09-03), Last Collected is the last time data actually arrived.
  // Default-visible: a column behind the Columns menu reads as not shipped
  // (IR-1233 r6), and this one IS the ticket.
  { key: "last_collection", label: "Last Collected" },
  { key: "created", label: "Created" },
  { key: "cover", label: "Cover" },
  { key: "imei", label: "IMEI #1" },
  { key: "imei_2", label: "IMEI #2" },
  { key: "model", label: "Model" },
  // IR-1610 (Rotem, 2026-09-14): "move the old Groups column to be last in
  // table". The assigned-group count is the number she reads LEAST often now
  // that Collected groups / Delivering / Archived / Stopped each answer a
  // sharper question, so it goes to the end rather than competing for the
  // left-hand columns. Its capacity readout did NOT come with it — IR-1610 moves
  // that onto Collected groups (see `collected` above), so this cell is once
  // again the plain assigned count it was before IR-1226.
  { key: "groups", label: "Groups" },
];

// cover + imei + model start hidden — opt-in detail columns. IR-1307's
// classification is DEFAULT-VISIBLE: Rotem asked for it to "show in Avatar
// table !", and a column hidden behind the Columns menu reads as not shipped
// (IR-1233 r6: a match in a default-hidden column looked like a broken search).
// IR-1351's notes is default-visible for the SAME reason and more strongly:
// the toolbar search now matches notes, so a hidden Notes column would return
// rows with no visible reason for being in the result set.
//
// IR-1479 r3: listed in the same relative order as AVATAR_COLUMNS. Only the
// KEYS are persisted (and the render order comes from AVATAR_COLUMNS), so this
// reorder mints no new storage version.
export const DEFAULT_VISIBLE: AvatarColumnKey[] = [
  "platform",
  "status",
  "collection",
  // IR-1697 r14: directly after Collection (see AVATAR_COLUMNS).
  "ingestion",
  // IR-1426: default-visible — the ask IS to see the phone-reported number, and
  // a column behind the Columns menu reads as not shipped (IR-1233 r6). It
  // renders "—" fleet-wide until the backend reaches prod; that is the honest
  // pre-deploy state, not an empty column.
  "collected",
  // IR-1400 r6: default-visible — the delivering number IS the reopen's ask,
  // and a column behind the Columns menu reads as not shipped (IR-1233 r6).
  "delivering",
  // IR-1487: default-visible for the same reason as every column before it —
  // the ask IS to see this number, and a column behind the Columns menu reads
  // as not shipped (IR-1233 r6). It renders "—" for all but the V2 Telegram
  // avatars, which is the honest state, not an empty column.
  "archived",
  // IR-1479: default-visible for the same reason as every column before it —
  // an avatar short of its own phone is invisible on every other figure, so a
  // column behind the Columns menu would leave it exactly as invisible as it
  // is today (IR-1233 r6). It renders "—" for the V1 fleet, which publishes no
  // per-cycle count; that is the honest state, not an empty column.
  "not_collected",
  // IR-1502: default-visible for the same reason as every column before it — the
  // ask IS to see the trend, and a column behind the Columns menu reads as not
  // shipped (IR-1233 r6). It renders "unknown" fleet-wide until the collector
  // ships its first reading; that is the honest state, not an empty column.
  "load",
  // IR-1696: default-visible for the same reason as every column before it —
  // "the most critical 5 min bug" IS the ask, and a column behind the Columns
  // menu reads as not shipped (IR-1233 r6).
  "analyst",
  "classification",
  // IR-1351: default-visible for the same reason as classification and notes —
  // Rotem asked for it to show in the list, and a column behind the Columns menu
  // reads as not shipped. imei_2 stays opt-in alongside imei.
  "geo",
  // IR-1351: default-visible — the ask is literally to SEE the proxy while
  // filtered to failed avatars, and a column behind the Columns menu reads as
  // not shipped (IR-1233 r6).
  "proxy",
  // IR-1385: default-visible for the same reason — the ask IS to see which
  // generation each avatar runs, and a column behind the Columns menu reads as
  // not shipped (IR-1233 r6).
  "scraper",
  "notes",
  "last_seen",
  // IR-1515: default-visible for the same reason as every column before it —
  // "add Date for disconnected" IS the ask, and a column behind the Columns
  // menu reads as not shipped (IR-1233 r6).
  "last_collection",
  "created",
  // IR-1610: still default-visible — the column moved to the END of the table,
  // it was not hidden. A column behind the Columns menu reads as not shipped
  // (IR-1233 r6), and "last" is a position, not a demotion.
  "groups",
];

const STORAGE_KEY = "avatars-columns-v15";

// Every operator who has opened this tab has a saved preference list, and that
// list cannot mention a column that did not exist when they saved it. Adding a
// new key to DEFAULT_VISIBLE alone therefore leaves it INVISIBLE for exactly
// the people who asked for it (IR-1307 hit this with `classification`). So each
// time a column is added we mint a new storage key and carry the operator's
// show/hide choices forward from the previous one, re-adding only the columns
// that postdate it. Bumping the key without migrating would be simpler but
// would silently reset everyone's hidden columns.
//
// Newest legacy key first; `addedSince` is everything introduced AFTER that key
// was minted, so a very old payload picks up every column it never saw.
// Ordered oldest → newest: each entry is a storage key that has existed, and the
// columns minted AFTER it. `addedSince` is DERIVED from this, so adding a
// version cannot leave an older key half-migrated.
//
// It was hand-maintained until v5, and that is exactly how it broke: the v1
// entry silently missed `proxy` because a formatter had collapsed it onto one
// line and the edit no longer matched. An operator on a v1 list would have been
// the only one who never saw the Proxy column — the same "invisible to exactly
// the people who asked" failure the chain exists to prevent, now one level up.
//
// ⚠️ Only DEFAULT-VISIBLE additions belong here. `imei_2` is deliberately absent:
// it is opt-in like `imei`, so its absence from an older list is the correct
// default rather than "never saw it", and migrating it in would re-show a detail
// column an operator had deliberately hidden.
const VERSION_HISTORY: { key: string; addedAfter: AvatarColumnKey[] }[] = [
  { key: "avatars-columns-v1", addedAfter: ["classification"] }, // IR-1307
  { key: "avatars-columns-v2", addedAfter: ["notes"] }, // IR-1351 item 1
  { key: "avatars-columns-v3", addedAfter: ["geo"] }, // IR-1351 item 2
  { key: "avatars-columns-v4", addedAfter: ["proxy"] }, // IR-1351 item 4
  { key: "avatars-columns-v5", addedAfter: ["scraper"] }, // IR-1385
  { key: "avatars-columns-v6", addedAfter: ["delivering"] }, // IR-1400 r6
  { key: "avatars-columns-v7", addedAfter: ["collected"] }, // IR-1426
  { key: "avatars-columns-v8", addedAfter: ["archived"] }, // IR-1487
  { key: "avatars-columns-v9", addedAfter: ["not_collected"] }, // IR-1479
  { key: "avatars-columns-v10", addedAfter: ["last_collection"] }, // IR-1515
  { key: "avatars-columns-v11", addedAfter: ["load"] }, // IR-1502
  // IR-1610 r1 minted NO new version: it only MOVED `groups` to the end of
  // the table, and position comes from AVATAR_COLUMNS while storage holds
  // keys alone. IR-1610 r2 DID mint one (v13 → v14) for a Status breakdown
  // column, which r5 took away again — so v13 now has nothing to add.
  { key: "avatars-columns-v12", addedAfter: ["analyst"] }, // IR-1696
  { key: "avatars-columns-v13", addedAfter: [] }, // IR-1610 r2 (column since removed)
  // IR-1610 r5 (Rotem, 2026-10-02) REMOVED `breakdown` — it moved into the
  // strip under each avatar. v15 is minted so a saved v14 list holding the
  // dead key is retired; the operator's other show/hide choices carry forward
  // through the migration below (`parseStored` drops any key that is no
  // longer in AVATAR_COLUMNS).
  { key: "avatars-columns-v14", addedAfter: [] }, // IR-1610 r5
];

// Newest legacy key first — the first one that matches wins, so a more recent
// (more deliberate) preference is never overridden by an older leftover.
export const LEGACY_KEYS: { key: string; addedSince: AvatarColumnKey[] }[] = VERSION_HISTORY.map(
  (v, i) => ({
    key: v.key,
    addedSince: VERSION_HISTORY.slice(i).flatMap((x) => x.addedAfter),
  }),
).reverse();

type ReadableStorage = Pick<Storage, "getItem">;
type WritableStorage = Pick<Storage, "setItem">;

function parseStored(raw: string | null): AvatarColumnKey[] | null {
  if (!raw) return null;
  const parsed: unknown = JSON.parse(raw);
  if (!Array.isArray(parsed)) return null;
  const valid = new Set<string>(AVATAR_COLUMNS.map((c) => c.key));
  return parsed.filter((k): k is AvatarColumnKey => typeof k === "string" && valid.has(k));
}

export function loadVisibleColumns(storage: ReadableStorage = localStorage): AvatarColumnKey[] {
  try {
    const current = parseStored(storage.getItem(STORAGE_KEY));
    if (current) return current;
    for (const { key, addedSince } of LEGACY_KEYS) {
      const legacy = parseStored(storage.getItem(key));
      if (!legacy) continue;
      // Preserve their layout, but surface anything added since that key —
      // keeping AVATAR_COLUMNS order so migrated columns land where they belong.
      const keep = new Set<AvatarColumnKey>([...legacy, ...addedSince]);
      return AVATAR_COLUMNS.map((c) => c.key).filter((k) => keep.has(k));
    }
    return DEFAULT_VISIBLE;
  } catch {
    return DEFAULT_VISIBLE;
  }
}

export function saveVisibleColumns(
  cols: AvatarColumnKey[],
  storage: WritableStorage = localStorage,
): void {
  try {
    storage.setItem(STORAGE_KEY, JSON.stringify(cols));
  } catch {
    // quota / private mode — persistence is best-effort
  }
}
